<template>
  <ed-modal
    :title="claimMode ? 'Claim Receipt' : 'Submit Receipt'"
    :show="show"
    @close="handleClose"
    :primary-button="claimMode ? 'CLAIM' : 'SUBMIT'"
    secondary-button="CANCEL"
    @confirm="handleMatch"
    :primary-button-disabled="!selectedCandidate || actionInFlight"
    type="medium-large"
  >
    <div slot="body" class="candidate-selector-body">
      <p v-if="!isMobile" class="modal-subtitle">Upload an image of a receipt and it will be matched to a transaction and coded automatically.</p>

      <!-- Auto-linked banner — dismissable -->
      <div v-if="autoLinked && !dismissedAutoLink" class="alert alert-info auto-linked-banner">
        <i class="fas fa-info-circle"></i>
        <span>This receipt was automatically matched. Confirm or select a different transaction.</span>
        <a href="javascript:;" class="dismiss-auto-link" @click="dismissedAutoLink = true">Dismiss</a>
      </div>

      <!-- Receipt image (left) + Receipt Data (right), side-by-side -->
      <div class="receipt-row">
        <!-- Receipt image preview (left column) -->
        <div class="receipt-section">
          <h4 class="section-label">Receipt</h4>
          <ReceiptImageViewer
            :image-url="displayImageUrl"
            :content-type="localImageContentType"
            :errored="receiptImageError"
          />
        </div>

        <!-- Receipt Data (OCR summary) (right column, vertical key/value) -->
        <div class="receipt-data-section">
          <h4 class="section-label">Receipt Data</h4>
          <div class="receipt-data-stack">
            <div class="data-field">
              <div class="field-label">Merchant</div>
              <div v-if="loading" class="field-value">
                <span class="skeleton skeleton-line skeleton-merchant"></span>
              </div>
              <div v-else class="field-value">{{ ocrMerchant || '—' }}</div>
            </div>
            <div class="data-field">
              <div class="field-label">Date</div>
              <div v-if="loading" class="field-value">
                <span class="skeleton skeleton-line skeleton-date"></span>
              </div>
              <div v-else class="field-value">{{ ocrDate || '—' }}</div>
            </div>
            <div class="data-field">
              <div class="field-label">Amount</div>
              <div v-if="loading" class="field-value">
                <span class="skeleton skeleton-line skeleton-amount"></span>
              </div>
              <div v-else class="field-value">{{ ocrAmount || '—' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile only — Folder CTA above the list per Figma 533:287 -->
      <div v-if="isMobile && !claimMode" class="receipt-folder-prompt receipt-folder-prompt--top">
        <span class="prompt-text">Don’t see the transaction?</span>
        <button
          type="button"
          class="btn btn-secondary add-to-folder-btn"
          :disabled="actionInFlight"
          @click="handleSaveToFolder"
        >
          ADD TO RECEIPT FOLDER
        </button>
      </div>

      <!-- Open and Pending Transactions -->
      <div class="transactions-section">
        <h4 class="section-label">Open and Pending Transactions</h4>

        <!-- Web view: table -->
        <!-- TODO(productdev-5606): column order vs Figma — pending Tudor decision -->
        <table v-if="!isMobile" class="candidates-table">
          <thead>
            <tr>
              <th class="select-col"></th>
              <th class="date-col">Date</th>
              <th class="card-col">Card</th>
              <th class="holder-col">Card Holder</th>
              <th class="merchant-col">Merchant</th>
              <th class="amount-col">Amount</th>
              <th class="status-col">Transaction Status</th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr v-for="n in SKELETON_ROW_COUNT" :key="'skeleton-row-' + n" class="skeleton-row">
              <td class="select-col"></td>
              <td class="date-col"><span class="skeleton skeleton-line"></span></td>
              <td class="card-col"><span class="skeleton skeleton-line"></span></td>
              <td class="holder-col"><span class="skeleton skeleton-line"></span></td>
              <td class="merchant-col"><span class="skeleton skeleton-line"></span></td>
              <td class="amount-col"><span class="skeleton skeleton-line"></span></td>
              <td class="status-col"><span class="skeleton skeleton-line"></span></td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr
              v-for="candidate in candidates"
              :key="candidate.legacyTransactionId"
              :class="{ 'is-selected': selectedCandidate && selectedCandidate.legacyTransactionId === candidate.legacyTransactionId }"
              @click="selectedCandidate = candidate"
            >
              <td class="select-col">
                <input
                  type="radio"
                  :id="'candidate-' + candidate.legacyTransactionId"
                  :value="candidate"
                  v-model="selectedCandidate"
                  class="candidate-radio"
                />
              </td>
              <td class="date-col">{{ formatDate(candidate.transactionDate) }}</td>
              <td class="card-col">
                <span v-if="candidate.cardNumberLast4">*{{ candidate.cardNumberLast4 }}</span>
                <span v-else>—</span>
              </td>
              <td class="holder-col">
                <span class="holder-name">{{ candidate.cardholderName || '—' }}</span>
                <span v-if="showNameOnCard(candidate)" class="holder-name-on-card">{{ candidate.nameOnCard }}</span>
              </td>
              <td class="merchant-col">{{ candidate.merchantName || '—' }}</td>
              <td class="amount-col">{{ formatCurrency(candidate.amount, candidate.currencyCode) }}</td>
              <td class="status-col">{{ candidate.transactionStatus || 'Open' }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Mobile view: list -->
        <div v-if="isMobile && loading" class="candidates-list">
          <div v-for="n in SKELETON_ROW_COUNT" :key="'skeleton-mobile-' + n" class="candidate-item skeleton-row">
            <span class="skeleton skeleton-line skeleton-mobile-row"></span>
          </div>
        </div>
        <div v-else-if="isMobile" class="candidates-list">
          <div
            v-for="candidate in candidates"
            :key="candidate.legacyTransactionId"
            class="candidate-item"
            :class="{ 'is-selected': selectedCandidate && selectedCandidate.legacyTransactionId === candidate.legacyTransactionId }"
          >
            <label class="candidate-label">
              <input
                type="radio"
                :id="'candidate-mobile-' + candidate.legacyTransactionId"
                :value="candidate"
                v-model="selectedCandidate"
                class="candidate-radio"
              />
              <div class="candidate-details">
                <div class="merchant-name">
                  {{ candidate.merchantName }}
                  <span v-if="candidate.cardNumberLast4" class="card-last4">•••• {{ candidate.cardNumberLast4 }}</span>
                </div>
                <div class="transaction-meta">
                  <span class="amount">{{ formatCurrency(candidate.amount, candidate.currencyCode) }}</span>
                  <span class="separator">•</span>
                  <span class="date">{{ formatDate(candidate.transactionDate) }}</span>
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="!loading && (!candidates || candidates.length === 0)" class="empty-state">
          <p>No matching transactions found. Add this receipt to your folder.</p>
        </div>
      </div>

      <!-- Inline Add to Receipt Folder -->
      <div v-if="!claimMode && !loading" class="receipt-folder-prompt">
        <span class="prompt-text">Don’t see the transaction?</span>
        <button
          type="button"
          class="btn btn-secondary add-to-folder-btn"
          :disabled="actionInFlight"
          @click="handleSaveToFolder"
        >
          ADD TO RECEIPT FOLDER
        </button>
      </div>
    </div>
  </ed-modal>
</template>

<script>
import receiptScansApi from '@/core/api/receiptScans'
import ReceiptImageViewer from '@/components/_modules/Programs/Card-transactions/ReceiptImageViewer.vue'

export default {
  name: 'CandidateSelector',

  components: {
    ReceiptImageViewer
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    // optional — modal is hidden via :show until a scan is in flight
    receiptScanId: {
      type: String
    },
    candidates: {
      type: Array,
      default: () => []
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    rowVersion: {
      type: String,
      default: null
    },
    autoLinked: {
      type: Boolean,
      default: false
    },
    claimMode: {
      type: Boolean,
      default: false
    },
    autoFill: {
      type: Object,
      default: null
    },
    // True while the Photon scan is in flight — render skeletons for OCR + table.
    loading: {
      type: Boolean,
      default: false
    },
    // Optional client-side objectURL for the receipt the user just uploaded.
    // When set, takes precedence over the backend-fetched `receiptImageUrl` so the
    // receipt is visible immediately when the modal opens (before the BE image
    // fetch resolves). Caller is responsible for `URL.revokeObjectURL` on close.
    localImageUrl: {
      type: String,
      default: null
    },
    // MIME type of `localImageUrl` (e.g. 'application/pdf', 'image/png'). Drives
    // whether the receipt area renders <img> or <embed>. <img> can't display PDFs.
    localImageContentType: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      selectedCandidate: null,
      dismissedAutoLink: false,
      isMatching: false,
      isSavingToFolder: false,
      receiptImageUrl: null,
      receiptImageError: false,
      SKELETON_ROW_COUNT: 3
    }
  },

  // `_imageRequestSeq` is assigned directly on the instance (not declared in
  // data) so it stays non-reactive — Vue 2 skips proxying `_`-prefixed keys,
  // and keeping it off data() also avoids template-binding mistakes. Used as a
  // request sequence counter so a late fetchReceiptImage response from a prior
  // open can't overwrite the current one (reopen-close-reopen race).
  created () {
    this._imageRequestSeq = 0
  },

  computed: {
    actionInFlight() {
      return this.isMatching || this.isSavingToFolder
    },
    // Prefer the caller-supplied local objectURL (instant render at modal-open)
    // over the BE-fetched presigned URL. They reference the same bytes — once the
    // BE image arrives we could swap to it, but keeping the local one is cheaper
    // and visually identical.
    displayImageUrl() {
      return this.localImageUrl || this.receiptImageUrl
    },
    ocrMerchant() {
      return this.autoFill && this.autoFill.merchantName ? this.autoFill.merchantName : null
    },
    ocrDate() {
      var d = this.autoFill && this.autoFill.transactionDate
      if (!d) return null
      try {
        var dt = new Date(d)
        var mm = String(dt.getMonth() + 1).padStart(2, '0')
        var dd = String(dt.getDate()).padStart(2, '0')
        return mm + '/' + dd + '/' + dt.getFullYear()
      } catch (e) {
        return d
      }
    },
    ocrAmount() {
      if (!this.autoFill || !this.autoFill.amount) return null
      var total = this.autoFill.amount.total
      if (typeof total !== 'number') return null
      var currency = this.autoFill.amount.currencyCode || 'USD'
      // PRODUCTDEV-5783 — thousand separators on the Receipt Data amount (e.g. "1,452.50 USD").
      return total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        if (this.candidates && this.candidates.length > 0) {
          const suggested = this.candidates.find(c => c.isSuggested)
          // Pre-select only the floor-clearing suggestion; none → select nothing so the user
          // picks, instead of confidently pre-selecting the top noise candidate (PRODUCTDEV-5475).
          this.selectedCandidate = suggested || null
        }
        this.fetchReceiptImage()
      } else {
        this.receiptImageUrl = null
        this.receiptImageError = false
      }
    },
    candidates(newVal) {
      if (!newVal || newVal.length === 0) {
        this.selectedCandidate = null
        return
      }
      // Preserve the user's current selection when the candidates list refreshes
      // (e.g. a poll tick) and their pick is still present. Resetting unconditionally
      // clobbers a deliberate user choice.
      if (this.selectedCandidate &&
          newVal.find(c => c.legacyTransactionId === this.selectedCandidate.legacyTransactionId)) {
        return
      }
      const suggested = newVal.find(c => c.isSuggested)
      this.selectedCandidate = suggested || null
    }
  },

  methods: {
    formatCurrency(amount, currencyCode) {
      if (typeof amount !== 'number') {
        return '$0.00'
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format(amount)
    },

    // PRODUCTDEV-5783 — show the card's optional custom "Name on Card" in gray under the
    // cardholder name, mirroring the transactions grid. Only when present AND different from
    // the holder (matches Card-cards/CardHolder.vue).
    showNameOnCard(candidate) {
      var name = (candidate && candidate.nameOnCard ? candidate.nameOnCard : '').trim()
      var holder = (candidate && candidate.cardholderName ? candidate.cardholderName : '').trim()
      return !!name && name !== holder
    },

    formatDate(dateStr) {
      if (!dateStr) {
        return 'N/A'
      }
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) {
        return dateStr
      }
      const momentDateFormat = (this.$root.getUser && this.$root.getUser.sessionSettings &&
        this.$root.getUser.sessionSettings.DateAndTimeFormats &&
        this.$root.getUser.sessionSettings.DateAndTimeFormats.MomentDateFormat) || 'MM-DD-YYYY'
      return global.moment(date).format(momentDateFormat)
    },

    formatConfidence(confidence) {
      if (typeof confidence === 'number') {
        const pct = confidence > 1 ? confidence : confidence * 100
        return Math.round(pct)
      }
      return confidence
    },

    confidenceClass(confidence) {
      const conf = typeof confidence === 'number' ? confidence : 0
      const normalized = conf > 1 ? conf / 100 : conf
      if (normalized >= 0.90) {
        return 'confidence-high'
      } else if (normalized >= 0.70) {
        return 'confidence-medium'
      }
      return 'confidence-low'
    },

    async handleMatch() {
      if (!this.selectedCandidate) {
        return
      }

      // Guard against double-submit: ed-modal's confirm() can fire more than once
      // (rapid double-click / Enter key) before the await settles and isMatching is
      // cleared in finally. Without this, MATCH/CLAIM could double-fire on the same scan.
      // Broadened to actionInFlight so a MATCH can't race an in-flight save-to-folder.
      if (this.actionInFlight) {
        return
      }

      // Capture state to locals BEFORE the await — ed-modal's confirm() fires
      // 'close' synchronously after 'confirm' (edModal/index.vue:161-163), and
      // handleClose() resets selectedCandidate/receiptScanId/rowVersion. When
      // the await resumes, `this.selectedCandidate` is null, and reading
      // `.legacyTransactionId` off it throws a TypeError. The catch's
      // error.response-less branch then misclassifies that as a Network error
      // and fires emit('match-error') → DELETE on the just-matched scan.
      const capturedReceiptScanId = this.receiptScanId
      const capturedLegacyTxId = this.selectedCandidate.legacyTransactionId
      const capturedRowVersion = this.rowVersion

      this.isMatching = true
      // Tell the parent a match is in flight so handleCandidateSelectorClose
      // (which fires from ed-modal's auto-close on @confirm) skips its
      // dispose-on-cancel path. Emit synchronously before the await so the
      // flag is set before the close event reaches the parent.
      this.$emit('match-attempt', { receiptScanId: capturedReceiptScanId })
      try {
        if (this.claimMode) {
          await receiptScansApi.claimReceipt(
            capturedReceiptScanId,
            capturedLegacyTxId,
            capturedRowVersion
          )
          this.$emit('claim-selected', {
            receiptScanId: capturedReceiptScanId,
            legacyTransactionId: capturedLegacyTxId
          })
        } else {
          await receiptScansApi.matchToTransaction(
            capturedReceiptScanId,
            capturedLegacyTxId,
            capturedRowVersion
          )
          this.$emit('match-selected', {
            receiptScanId: capturedReceiptScanId,
            legacyTransactionId: capturedLegacyTxId
          })
        }

        if (!this.claimMode) {
          this.handleClose()
        }
      } catch (error) {
        var errorEvent = this.claimMode ? 'claim-error' : 'match-error'

        if (error.response) {
          var status = error.response.status

          if (status === 403) {
            this.$notifications.notify({
              type: 'danger',
              message: 'You no longer have access to this receipt',
              placement: { from: 'top', align: 'center' }
            })
            this.$emit(errorEvent, { status: status, message: 'You no longer have access to this receipt' })
          } else if (status === 409) {
            var conflictMsg = this.claimMode
              ? 'Receipt state changed — pick again'
              : 'This receipt has already been matched'
            this.$notifications.notify({
              type: 'warning',
              message: conflictMsg,
              placement: { from: 'top', align: 'center' }
            })
            this.$emit(errorEvent, { status: status, message: conflictMsg })
          } else {
            var failMsg = this.claimMode ? 'Failed to claim receipt. Please try again.' : 'Failed to match receipt. Please try again.'
            this.$notifications.notify({
              type: 'danger',
              message: failMsg,
              placement: { from: 'top', align: 'center' }
            })
            this.$emit(errorEvent, { status: status, message: error.response.statusText || 'Unknown error' })
          }
        } else {
          this.$notifications.notify({
            type: 'danger',
            message: 'Network error. Please check your connection and try again.',
            placement: { from: 'top', align: 'center' }
          })
          this.$emit(errorEvent, { status: 0, message: 'Network error' })
        }
      } finally {
        this.isMatching = false
      }
    },

    async handleSaveToFolder() {
      // Mirror handleMatch's double-submit guard: ignore taps while either a
      // match or a prior save-to-folder is still settling.
      if (this.actionInFlight) {
        return
      }

      this.isSavingToFolder = true
      try {
        await receiptScansApi.saveToFolder(this.receiptScanId, this.rowVersion)

        this.$emit('save-to-folder', {
          receiptScanId: this.receiptScanId
        })

        this.$notifications.notify({
          // 'info' keeps the notification chrome neutral (no colored border) — the green
          // visual is carried entirely by the inline fa-check-circle icon, which is a
          // solid green tick-in-circle glyph.
          type: 'info',
          message: '<i class="fas fa-check-circle" style="color: #5cb85c; margin-right: 8px;"></i>Receipt added to Receipt Folder',
          placement: { from: 'top', align: 'center' }
        })

        this.handleClose()
      } catch (error) {
        console.error('Error saving to folder:', error)
        this.$notifications.notify({
          type: 'danger',
          message: 'Failed to save receipt. Please try again.',
          placement: { from: 'top', align: 'center' }
        })
      } finally {
        this.isSavingToFolder = false
      }
    },

    handleClose() {
      // Reset state
      this.selectedCandidate = null
      this.dismissedAutoLink = false
      this.isMatching = false
      this.isSavingToFolder = false
      this.receiptImageUrl = null
      this.receiptImageError = false

      this.$emit('close')
    },

    async fetchReceiptImage() {
      if (!this.receiptScanId) return
      // Bump the sequence and stash locally. Any later await-resume on a stale
      // request (modal closed and reopened on a new scan) finds seq !== current
      // and bails before writing receiptImageUrl, which prevents a stale image
      // from landing in the new context.
      const seq = ++this._imageRequestSeq
      this.receiptImageError = false
      try {
        var res = await receiptScansApi.getImage(this.receiptScanId)
        if (seq !== this._imageRequestSeq || !this.show) return
        if (res && res.presignedUrl) {
          this.receiptImageUrl = res.presignedUrl
        } else {
          this.receiptImageError = true
        }
      } catch (err) {
        if (seq !== this._imageRequestSeq || !this.show) return
        console.error('Failed to fetch receipt image:', err)
        this.receiptImageError = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-selector-body {
  padding: 4px 0 0;

  .modal-subtitle {
    color: #333;
    font-size: 14px;
    line-height: 25px;
    letter-spacing: 0.18px;
    margin: 0 0 20px;
    // PRODUCTDEV-5783 — keep the subtitle on a single row.
    white-space: nowrap;
  }

  .section-label {
    font-family: inherit;
    font-weight: 600;
    font-size: 16px;
    color: #333;
    margin: 0 0 8px;
    line-height: 20px;
  }

  .receipt-row {
    display: flex;
    gap: 13px;
    align-items: stretch;
    margin-bottom: 24px;
  }

  .receipt-section {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .receipt-data-section {
    flex: 1 1 0;
    min-width: 0;

    .receipt-data-stack {
      background-color: #fafafa;
      padding: 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 20px;
      align-items: stretch;
    }

    .data-field {
      display: flex;
      flex-direction: column;

      .field-label {
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
        color: #333;
        margin-bottom: 4px;
      }

      .field-value {
        font-size: 13.6px;
        line-height: 16px;
        color: #333;
        min-height: 16px;
      }
    }
  }

  // Skeleton shimmer (adapted from Card-list/ShowCardNumber.vue:327-342)
  .skeleton {
    display: inline-block;
    background: linear-gradient(90deg, #F0F0F0 25%, #f7f7f7 50%, #F0F0F0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
  }

  .skeleton-line {
    height: 14px;
    width: 80px;
    vertical-align: middle;
  }

  .skeleton-merchant { width: 140px; }
  .skeleton-date { width: 90px; }
  .skeleton-amount { width: 100px; }

  .skeleton-row {
    cursor: default;
    pointer-events: none;
  }

  .skeleton-mobile-row {
    width: 100%;
    height: 32px;
  }

  .transactions-section {
    margin-bottom: 16px;
  }

  .auto-linked-banner {
    margin-bottom: 16px;
    padding: 12px 16px;
    border-left: 4px solid #17a2b8;
    background-color: #f8f9fa;
    color: #155724;
    border-radius: 4px;
    font-size: 14px;

    i {
      margin-right: 8px;
    }

    .dismiss-auto-link {
      margin-left: 12px;
      color: #666;
      font-size: 12px;
    }
  }

  // Web table view
  .candidates-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 12px;
    border: 1px solid #ececec;

    thead {
      background-color: #f7f7f7;
      border-bottom: 1px solid #ececec;

      th {
        padding: 10px 8px;
        text-align: left;
        font-weight: 600;
        font-size: 12px;
        color: #333;
        line-height: 16px;

        &.select-col { width: 28px; text-align: center; padding: 10px 4px; }
        &.date-col { width: 90px; }
        &.card-col { width: 60px; }
        &.holder-col { width: 110px; }
        &.merchant-col { }
        &.amount-col { width: 90px; text-align: right; padding-right: 12px; }
        &.status-col { width: 130px; }
      }
    }

    tbody {
      tr {
        border-bottom: 1px solid #ececec;
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover { background-color: #f9f9f9; }

        // Figma "Rectangle Copy 18" — light blue selected row
        &.is-selected {
          background-color: #dbe9f9;
        }

        td {
          padding: 8px;
          font-size: 12px;
          line-height: 16px;
          color: #333;

          &.select-col {
            text-align: center;
            padding: 8px 4px;

            .candidate-radio {
              cursor: pointer;
              width: 14px;
              height: 14px;
              accent-color: #49316b;
            }
          }

          &.amount-col {
            text-align: right;
            padding-right: 12px;
          }

        }
      }
    }
  }

  // PRODUCTDEV-5783 — custom "Name on Card" under the holder, matching the transactions grid
  // CardHolder cell: small + secondary gray, only rendered when it differs from the holder.
  .holder-col {
    .holder-name { display: block; line-height: 1.2; }
    .holder-name-on-card {
      display: block;
      font-size: 0.85em;
      color: #888;
      line-height: 1.2;
    }
  }

  .receipt-folder-prompt {
    display: flex;
    align-items: center;
    gap: 13px;
    margin-top: 4px;
    margin-bottom: 4px;

    .prompt-text {
      color: #333;
      font-size: 14px;
      line-height: 25px;
      letter-spacing: 0.18px;
    }

    .add-to-folder-btn {
      background-color: #f8f8f8;
      border: 1px solid #999;
      border-radius: 3px;
      color: #333;
      font-weight: 600;
      font-size: 14px;
      letter-spacing: 0.18px;
      text-transform: uppercase;
      padding: 6px 17px;
      height: 32px;
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover:not(:disabled) { background-color: #efefef; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }

  // Mobile list view
  .candidates-list {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .candidate-item {
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 12px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #4a90e2;
        background-color: #f9f9f9;
      }

      &.is-suggested {
        background-color: #f0f8ff;
        border-color: #4a90e2;
        border-left: 3px solid #4a90e2;
      }

      &.is-selected {
        background-color: #e6f2ff;
        border-color: #0052cc;
        border-left: 3px solid #0052cc;
      }

      .candidate-label {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        cursor: pointer;
        margin: 0;

        .candidate-radio {
          margin-top: 3px;
          width: 18px;
          height: 18px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .candidate-details {
          flex: 1;
          min-width: 0;

          .merchant-name {
            font-weight: 500;
            font-size: 14px;
            color: #333;
            margin-bottom: 6px;
          }

          .transaction-meta {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            font-size: 12px;
            color: #666;

            .amount {
              font-family: 'Courier New', monospace;
              font-weight: 500;
            }

            .separator {
              color: #ccc;
            }

            .confidence-badge {
              margin-left: auto;
              flex-shrink: 0;
            }
          }
        }
      }
    }
  }

  // Confidence badge
  .confidence-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;

    &.confidence-high {
      background-color: #d4edda;
      color: #155724;
    }

    &.confidence-medium {
      background-color: #fff3cd;
      color: #856404;
    }

    &.confidence-low {
      background-color: #f8d7da;
      color: #721c24;
    }
  }

  // Empty state
  .empty-state {
    padding: 40px 20px;
    text-align: center;
    color: #666;

    p {
      margin: 0;
      font-size: 14px;
    }
  }

  // Show suggestions link
  .show-suggestions-link {
    text-align: center;
    padding: 12px 0;
    border-top: 1px solid #eee;
    margin-top: 12px;

    a {
      color: #4a90e2;
      text-decoration: none;
      font-size: 13px;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

// Responsive adjustments
@media (max-width: 768px) {
  .candidate-selector-body {
    .receipt-row {
      flex-direction: column;
      gap: 16px;
    }

    .candidates-table {
      // On mobile, hide less important columns
      thead th.confidence-col,
      tbody td.confidence-col {
        display: none;
      }

      thead th.date-col,
      tbody td.date-col {
        font-size: 12px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-transactions/CandidateSelector.vue